import { useState } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AssaultIncidentRecord {
  id: number;
  person_id: number;
  agency_id: number;
  incident_date: string | null;
  location: string | null;
  perpetrator_id: number | null;
  details: string | null;
  sart_response: string | null;
  forensic_exam: string | null;
  created_by: number | null;
  created_at: string;
  author_first_name?: string;
  author_last_name?: string;
  perpetrator_first_name?: string;
  perpetrator_last_name?: string;
  perpetrator_relationship?: string;
}

export interface AssaultIncidentFormData {
  incidentDate?: string;
  location?: string;
  perpetratorId?: number;
  details?: string;
  sartResponse?: string;
  forensicExam?: string;
}

interface AssaultIncidentFormProps {
  initial?: AssaultIncidentRecord;
  onSubmit: (data: AssaultIncidentFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AssaultIncidentForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: AssaultIncidentFormProps) {
  const [incidentDate, setIncidentDate] = useState(initial?.incident_date ?? "");
  const [location, setLocation] = useState(initial?.location ?? "");
  const [details, setDetails] = useState(initial?.details ?? "");
  const [sartResponse, setSartResponse] = useState(initial?.sart_response ?? "");
  const [forensicExam, setForensicExam] = useState(initial?.forensic_exam ?? "");

  function handleSubmit() {
    onSubmit({
      incidentDate: incidentDate || undefined,
      location: location || undefined,
      details: details || undefined,
      sartResponse: sartResponse || undefined,
      forensicExam: forensicExam || undefined,
    });
  }

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-foreground">
        {initial ? "Edit Assault Incident" : "New Assault Incident"}
      </h4>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Incident Date"
          type="date"
          value={incidentDate}
          onChange={(e) => setIncidentDate(e.target.value)}
        />
        <Input
          label="Location"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Location of incident..."
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Incident Details
        </label>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={4}
          placeholder="Describe the incident details (text or JSON)..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          SART Response
        </label>
        <textarea
          value={sartResponse}
          onChange={(e) => setSartResponse(e.target.value)}
          rows={3}
          placeholder="SART activation details, response team info (text or JSON)..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Forensic Exam
        </label>
        <textarea
          value={forensicExam}
          onChange={(e) => setForensicExam(e.target.value)}
          rows={3}
          placeholder="Forensic exam details, results, status (text or JSON)..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="flex gap-3">
        <Button size="sm" onClick={handleSubmit} loading={isLoading}>
          {initial ? "Update Incident" : "Create Incident"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
